/**
 * Database entry preview for the map landmark panel: title + HTML body.
 * Maps (`.zwmap`) and other non-entry JSON are rejected. Body HTML matches
 * entity peek (`marked` over zwdoc projection / legacy markdown).
 */

import { marked } from "marked";
import { just, nothing, type Maybe } from "./maybe";
import { stripEntityFrontmatterBody } from "./entityBody";
import { isMapFileName } from "./mapDocument";
import { isZwDocText } from "./zwdoc/format";
import { projectionPeekMarkdown, scanTextOf } from "./zwdoc/scanText";

marked.setOptions({ gfm: true, breaks: false });

export type DatabaseEntryPreview = {
  /** Markdown source used for the body (tests / debugging). */
  markdown: string;
  /** Safe-ish HTML for a trusted project file (same as entity peek). */
  bodyHtml: string;
};

function renderLightMarkdown(source: string): string {
  try {
    return marked.parse(source, { async: false }) as string;
  } catch {
    return source;
  }
}

/**
 * True when `raw` is a worldbuilding entry we can preview (`.zwdoc` or legacy
 * markdown). Map JSON and other `{…}` blobs are not entries.
 */
export function isDatabaseEntryPreviewSource(
  fileName: string,
  raw: string,
): boolean {
  if (isMapFileName(fileName)) return false;
  if (isZwDocText(raw)) return true;
  const trimmed = raw.trimStart();
  if (trimmed.startsWith("{")) return false;
  return true;
}

/** Markdown body of a Database entry, or Nothing when the file is not an entry. */
export function databaseEntryMarkdown(
  raw: string,
  fileName: string,
): Maybe<string> {
  if (isDatabaseEntryPreviewSource(fileName, raw) === false) return nothing;
  const scan = scanTextOf(raw);
  const markdown =
    scan.kind === "zwdoc"
      ? projectionPeekMarkdown(scan.projection)
      : stripEntityFrontmatterBody(scan.text);
  return just(markdown.trim());
}

export function databaseEntryPreview(
  raw: string,
  fileName: string,
): Maybe<DatabaseEntryPreview> {
  const markdownM = databaseEntryMarkdown(raw, fileName);
  if (markdownM.tag === "nothing") return nothing;
  return just({
    markdown: markdownM.value,
    bodyHtml: renderLightMarkdown(markdownM.value),
  });
}
